import { CommonModule } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { StudentGetJobApplicationSubmittedData } from '@edtech-ui/core/models/student-job-management/student-application-submitted';
import { StudentJobService } from '@edtech-ui/core/services/student-job-management/student-job.service';
import { locationValidator } from '@edtech-ui/core/utils/resume-validation/resume-validation-personal';
import { StudentCoreExpertiseValidator } from '@edtech-ui/core/utils/student-apply-job/student-core-experties';
import { StudentCtcValidator } from '@edtech-ui/core/utils/student-apply-job/student-ctc.validator';
import { StudentExpectedCtcValidator } from '@edtech-ui/core/utils/student-apply-job/student-expected-ctc.validator';
import { StudentJobEmailValidator } from '@edtech-ui/core/utils/student-apply-job/student-job-email';
import { StudentCityPreferenceValidator } from '@edtech-ui/core/utils/student-apply-job/student-job-locations';
import { StudentJobApplicantNameValidator } from '@edtech-ui/core/utils/student-apply-job/student-job-name';
import { StudentJobTitleValidator } from '@edtech-ui/core/utils/student-apply-job/student-last-job-title';
import { StudentEducationValidator } from '@edtech-ui/core/utils/student-apply-job/student-education.validator';
import { ToastrService } from 'ngx-toastr';
import { AuthServiceService } from '@edtech-ui/core/services/authService/auth-service.service';
import { UserDetailResponse } from '@edtech-ui/core/models/Classroom/user-detail';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';
import { ResumeService } from '@edtech-ui/core/services/resume-service/resume.service';
import { ResumeListItem } from '@edtech-ui/core/models/resume-builder/get-user-resume';

@Component({
	selector: 'app-jobs-apply-form',
	standalone: true,
	imports: [CommonModule, ReactiveFormsModule, MatProgressSpinnerModule, RouterModule],
	templateUrl: './jobs-apply-form.component.html',
	styleUrl: './jobs-apply-form.component.scss'
})
export class JobsApplyFormComponent implements OnDestroy, OnInit {

	uploadedResume: File | null = null;
	uploadedResumeSize = '';
	existingResumeUrl: string | null = null;
	applyform: FormGroup;
	savedResumes: ResumeListItem[] = [];
	selectedType: 'fresher' | 'experienced' = 'fresher';
	jobId!: string;
	subscription = new Subscription();
	currentUserData: any = null;
	isLoading = false;
	isApplying = false;
	jobSubmitted: StudentGetJobApplicationSubmittedData | null = null;
	passingYears: number[] = [];
	isProfileTypeLocked = false;

	selectType(type: 'fresher' | 'experienced') {
		if (this.isProfileTypeLocked) return;
		this.selectedType = type;
		this.applyform.patchValue({ applicant_type: type });
		this.updateProfessionalFields(type);
	}

	updateProfessionalFields(type: 'fresher' | 'experienced') {
		const professionalFields = [
			'last_job_title', 'last_company_name', 'core_experties',
			'total_year_of_experience', 'current_ctc', 'expected_ctc',
			'city_pref_1', 'city_pref_2', 'city_pref_3'
		];

		professionalFields.forEach((field) => {
			const control = this.applyform.get(field);
			if (type === 'fresher') {
				control?.setValue('');
				control?.disable(); // Disable if fresher so it's excluded from validation
			} else {
				control?.enable(); // Enable if experienced so validation works, HTML readonly handles locking
			}
			control?.updateValueAndValidity();
		});
	}


	constructor(
		private router: Router,
		private route: ActivatedRoute,
		private studentJobService: StudentJobService,
		private toastr: ToastrService,
		private authService: AuthServiceService,
		private resumeService: ResumeService
	) {

		this.applyform = new FormGroup({
			applicant_name: new FormControl('', [Validators.required, StudentJobApplicantNameValidator()]),
			applicant_mobile: new FormControl('', [Validators.required, Validators.pattern('^[6-9][0-9]{9}$'), Validators.minLength(10), Validators.maxLength(10)]),
			applicant_email: new FormControl('', [Validators.required, StudentJobEmailValidator(), Validators.maxLength(100)]),
			applicant_location: new FormControl('', [Validators.required/*, locationValidator*/]),
			last_job_title: new FormControl('', [Validators.required, StudentJobTitleValidator()]),
			last_company_name: new FormControl('', [Validators.required, StudentJobTitleValidator()]),
			core_experties: new FormControl('', [Validators.required, StudentCoreExpertiseValidator()]),
			total_year_of_experience: new FormControl('', [Validators.required]),
			current_ctc: new FormControl('', [Validators.required, StudentCtcValidator()]),
			expected_ctc: new FormControl('', [Validators.required, StudentExpectedCtcValidator()]),
			city_pref_1: new FormControl('', [Validators.required, locationValidator, StudentCityPreferenceValidator()]),
			city_pref_2: new FormControl('', [Validators.required, locationValidator, StudentCityPreferenceValidator()]),
			city_pref_3: new FormControl('', [Validators.required, locationValidator, StudentCityPreferenceValidator()]),
			resume: new FormControl(null, [Validators.required]),
			consent: new FormControl(false, [Validators.requiredTrue]),
			applicant_type: new FormControl(),
			education: new FormControl('', [Validators.required]),
			college_name: new FormControl('', [Validators.required]),
			year_of_passing_out: new FormControl('', [Validators.required])
		});
		this.selectType('fresher');
	}

	handleProfileFieldClick() {
		this.toastr.warning('This information can only be updated from your Profile. Changes will reflect here automatically');
	}


	ngOnInit(): void {
		this.subscription.add(
			this.route.paramMap.subscribe(params => {
				this.jobId = params.get('jobId') || '';
				if (this.jobId) {
					this.getStudentJobApplicationSubmitted();
				}
			})
		);

		const storedId = localStorage.getItem('user_id');
		if (storedId) {
			this.getUserInfo(storedId);
		}

		this.applyform.get('current_ctc')?.valueChanges.subscribe(() => {
			this.applyform.get('expected_ctc')?.updateValueAndValidity();
		});

		this.generatePassingYears();

		this.subscription.add(
			this.resumeService.getStudentsResume(1, 10).subscribe(response => {
				if (response?.Success && response?.Data?.resumes) {
					this.savedResumes = response.Data.resumes;
				}
			})
		);
	}


	generatePassingYears(): void {
		const currentYear = new Date().getFullYear();
		this.passingYears = [];
		for (let year = currentYear + 4; year >= 1980; year--) {
			this.passingYears.push(year);
		}
	}


	checkMissingProfileFields(data: any): string[] {
		const missing: string[] = [];
		if (!data?.first_name?.trim()) {
			missing.push('first_name');
		}
		if (!data?.phone_email?.trim()) {
			missing.push('phone_email');
		}
		if (!data?.phone_number?.trim()) {
			missing.push('phone_number');
		}
		if (!data?.city?.trim()) {
			missing.push('city');
		}
		if (!data?.education_level?.trim()) {
			missing.push('education');
		}
		if (!data?.college_name?.trim()) {
			missing.push('college_name');
		}

		const pt = (data?.profile_type || '').toLowerCase();
		const isExperienced = pt === 'working' || pt === 'experienced' || pt === 'professional' || pt === 'working_professional';
		const isFresher = !isExperienced;

		if (isFresher) {
			if (!data?.year_of_passing?.trim()) {
				missing.push('year');
			}
		}

		if (isExperienced) {
			if (!data?.company_title?.trim()) missing.push('company_title');
			if (!data?.current_role?.trim()) missing.push('current_role');
			if (!data?.experience_years?.trim()) missing.push('experience');
		}

		if (!data?.skills || !Array.isArray(data?.skills) || data?.skills.length === 0) {
			missing.push('skills');
		}

		if (!data?.resume_url?.trim()) {
			missing.push('resume');
		}

		return missing;
	}

	onUpdateProfile() {
		const missingFields = this.checkMissingProfileFields(this.currentUserData || {});
		this.router.navigate(['/student-dashboard/profile'], {
			queryParams: {
				returnUrl: this.router.url,
				missingFields: missingFields.join(',')
			}
		});
	}

	getUserInfo(id: string) {
		this.subscription.add(
			this.authService.getUserDetails(id).subscribe({
				next: (response: UserDetailResponse) => {
					if (response.Success && response.Data) {
						this.currentUserData = response.Data;
						const missingFields = this.checkMissingProfileFields(response.Data);
						if (missingFields.length > 0) {
							this.toastr.warning('Please complete your profile details before applying.');
							this.router.navigate(['/student-dashboard/profile'], {
								queryParams: {
									returnUrl: this.router.url,
									missingFields: missingFields.join(',')
								}
							});
							return;
						}

						const firstName = (response.Data.first_name || '').trim().replace(/\s+/g, ' ');
						const lastName = (response.Data.last_name || '').trim().replace(/\s+/g, ' ');
						const fullName = `${firstName} ${lastName}`.trim().replace(/\s+/g, ' ');

						this.applyform.patchValue({
							applicant_name: fullName,
							applicant_email: (response.Data.phone_email || '').trim(),
							applicant_mobile: (response.Data.phone_number || '').trim()
						});

						const patchData: any = {};
						if (response.Data.city) patchData.applicant_location = response.Data.city.trim().replace(/\s+/g, ' ');
						if (response.Data.education_level) patchData.education = response.Data.education_level.trim();
						if (response.Data.college_name) patchData.college_name = response.Data.college_name.trim().replace(/\s+/g, ' ');
						if (response.Data.year_of_passing) patchData.year_of_passing_out = String(response.Data.year_of_passing).trim();

						// Professional Fields
						if (response.Data.company_title) patchData.last_company_name = response.Data.company_title.trim().replace(/\s+/g, ' ');
						if (response.Data.current_role) patchData.last_job_title = response.Data.current_role.trim().replace(/\s+/g, ' ');
						if (response.Data.tech_stack) patchData.core_experties = response.Data.tech_stack.trim().replace(/\s+/g, ' ');
						if (response.Data.experience_years) patchData.total_year_of_experience = response.Data.experience_years.trim();
						if (response.Data.current_ctc) patchData.current_ctc = String(response.Data.current_ctc).trim();
						if (response.Data.expected_ctc) patchData.expected_ctc = String(response.Data.expected_ctc).trim();
						if (response.Data.preferred_city1) patchData.city_pref_1 = response.Data.preferred_city1.trim().replace(/\s+/g, ' ');
						if (response.Data.preferred_city2) patchData.city_pref_2 = response.Data.preferred_city2.trim().replace(/\s+/g, ' ');
						if (response.Data.preferred_city3) patchData.city_pref_3 = response.Data.preferred_city3.trim().replace(/\s+/g, ' ');

						if (Object.keys(patchData).length > 0) {
							this.applyform.patchValue(patchData);
						}

						if (response.Data.profile_type) {
							const pt = response.Data.profile_type.toLowerCase();
							if (pt === 'fresher' || pt === 'student') {
								this.selectedType = 'fresher';
								this.applyform.patchValue({ applicant_type: 'fresher' });
								this.isProfileTypeLocked = true;
								// We already locked fields above, so updateProfessionalFields won't override because we can skip enabling them.
							} else if (pt === 'working' || pt === 'experienced' || pt === 'professional' || pt === 'working_professional') {
								this.selectedType = 'experienced';
								this.applyform.patchValue({ applicant_type: 'experienced' });
								this.isProfileTypeLocked = true;
								this.updateProfessionalFields('experienced');
							}
						}

						if (response.Data.resume_url) {
							this.existingResumeUrl = response.Data.resume_url;
							this.applyform.get('resume')?.clearValidators();
							this.applyform.get('resume')?.updateValueAndValidity();
						}
					}
				},
				error: () => this.toastr.error('Failed to load user details for autofill')
			})
		);
	}

	@HostListener('click', ['$event'])
	onClick(event: Event) {
		const target = event.target as HTMLElement;
		if (target.closest('.form-group')) {
			const input = target.closest('.form-group')?.querySelector('input[readonly], select[style*="pointer-events: none"]');
			if (input || (target.tagName === 'INPUT' && (target as HTMLInputElement).readOnly)) {
				this.handleProfileFieldClick();
			}
		}
	}

	goToResumeBuilderPage() {
		this.router.navigate(['student-dashboard/resume-builder'], {
			queryParams: {
				returnUrl: this.router.url
			}
		});
	}

	selectSavedResume(resumeId: string) {
		this.router.navigate(['/student-dashboard/resume-preview', resumeId], {
			queryParams: {
				returnUrl: this.router.url
			}
		});
	}


	goToJobDetailPage() {
		this.router.navigate(['student-dashboard/jobs-detail-page', this.jobId]);
	}


	onResumeUpload(event: any) {
		const file = event.target.files?.[0];

		if (!file) return;

		const allowedExtensions = ['pdf', 'doc', 'docx'];
		const fileExtension = file.name.split('.').pop()?.toLowerCase();

		if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
			this.toastr.error('Only PDF, DOC and DOCX files are allowed.');
			event.target.value = '';
			return;
		}

		const maxSize = 5 * 1024 * 1024;

		if (file.size > maxSize) {
			this.toastr.error('Resume file size cannot exceed 5 MB.');
			event.target.value = '';
			return;
		}

		const fileNameRegex = /^[a-zA-Z0-9\s._()-]+$/;

		if (!fileNameRegex.test(file.name)) {
			this.toastr.error('File name contains invalid characters.');
			event.target.value = '';
			return;
		}

		this.uploadedResume = file;
		this.applyform.patchValue({ resume: file });
		this.applyform.get('resume')?.updateValueAndValidity();
		const sizeInMB = (file.size / (1024 * 1024)).toFixed(2);
		this.uploadedResumeSize = `${sizeInMB} MB`;
	}


	removeResume() {
		this.uploadedResume = null;
		this.uploadedResumeSize = '';
		if (!this.existingResumeUrl) {
			this.applyform.get('resume')?.setValidators([Validators.required]);
		}
		this.applyform.patchValue({ resume: null });
		this.applyform.get('resume')?.updateValueAndValidity();
	}


	submitForm() {

		if (this.applyform.invalid) {
			this.applyform.markAllAsTouched();
			return;
		}
		this.isLoading = true;

		const formData = new FormData();
		const formValues = this.applyform.getRawValue();

		formData.append('applicant_name', (formValues.applicant_name || '').trim());
		formData.append('applicant_email', (formValues.applicant_email || '').trim());
		formData.append('applicant_mobile', (formValues.applicant_mobile || '').trim());
		formData.append('applicant_location', (formValues.applicant_location || '').trim());
		formData.append('education', (formValues.education || '').trim());
		formData.append('college_name', (formValues.college_name || '').trim());
		formData.append('year_of_passing_out', formValues.year_of_passing_out !== undefined && formValues.year_of_passing_out !== null ? String(formValues.year_of_passing_out).trim() : '');
		formData.append('applicant_type', this.selectedType);

		if (this.uploadedResume) {
			formData.append('resume', this.uploadedResume);
		}

		if (this.selectedType === 'experienced') {
			formData.append('last_job_title', (formValues.last_job_title || '').trim());
			formData.append('last_company_name', (formValues.last_company_name || '').trim());
			formData.append('core_experties', (formValues.core_experties || '').trim());
			formData.append('total_year_of_experience', (formValues.total_year_of_experience || '').trim());
			formData.append('current_ctc', (formValues.current_ctc || '').toString().trim());
			formData.append('expected_ctc', (formValues.expected_ctc || '').toString().trim());
			formData.append('city_pref_1', (formValues.city_pref_1 || '').trim());
			formData.append('city_pref_2', (formValues.city_pref_2 || '').trim());
			formData.append('city_pref_3', (formValues.city_pref_3 || '').trim());
		}

		this.subscription.add(
			this.studentJobService.postStudentApplyJobApplication(formData, this.jobId).pipe(
				tap((response) => {
					if (response?.Success) {
						this.toastr.success(response.Message);
						this.router.navigate(['student-dashboard/jobs/applied', this.jobId]);
					} else {
						this.toastr.error(response?.Message);
					}
				}),
				finalize(() => {
					this.isLoading = false;
				}),
				catchError(() => {
					this.toastr.error('Something went wrong. Please try again');
					return of(null);
				})
			).subscribe()
		);
	}


	getStudentJobApplicationSubmitted() {
		this.isApplying = true;
		this.subscription.add(
			this.studentJobService.getStudentJobApplicationSubmitted(this.jobId).pipe(
				tap(response => {
					if (response.Success) {
						this.jobSubmitted = response.Data;
					} else {
						this.toastr.error(response.Message);
					}
				}),
				finalize(() => {
					this.isApplying = false;
				}),
				catchError(() => {
					this.toastr.error('Something went wrong. Please try again');
					return of(null);
				}),
			).subscribe()
		)
	}


	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

}
